// @flow

import React, { Fragment } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useHistory } from 'react-router-dom';
import { Query } from 'shared_services/apollo/Query';
import classNames from 'classnames';
import LazyLoad from 'react-lazyload';
import { Section, Wrapper } from '@riseart/layout';
import { nl2br } from '@riseart/fe-utils';
import {
  MediaQuery,
  BlurredBanner,
  LazyloadPlaceholder,
  Heading,
  Paragraph,
  Link,
} from '@riseart/common';
import { Row, Col } from '@riseart/antd-provider';
import { ShareList } from '@riseart/share';
import { application as APP_CONFIG } from 'Config';
import { event as ENUM_EVENT, HTTP_STATUS, meta as META_ENUM } from 'Enum';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { generateHrefLangParams } from 'shared_services/riseart/meta/utils';
import { Share } from 'shared_components/common/share/Share';
import { Avatar } from 'shared_components/common/avatar/Avatar';
import { EventRsvpButton } from 'shared_components/common/event/Rsvp';
import { EmbeddedGoogleCalendarLink } from 'shared_components/common/embedded/calendar/GoogleLink';
import { EmmbeddedGoogleMaps } from 'shared_components/common/embedded/maps/Google';
import { generateSrcForCDN, Picture } from 'shared_components/common/artdirection/picture/Picture';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { Breadcrumbs } from 'shared_components/common/breadcrumbs/Breadcrumbs';
import { FormatDate } from 'shared_components/common/date/Format';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { gql404RedirectHandler } from 'shared_services/riseart/utils/RouteUtils';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { setTimeToDate } from 'shared_services/riseart/utils/Utils';
import { EventType } from 'shared_components/common/event/Type';
import { EventsRsvpProvider } from 'shared_data/providers/rsvp/Event';
import { CreateRsvpProvider } from 'shared_data/providers/rsvp/CreateRsvp';
import { EventCard } from 'shared_components/event/Card';
import READ_EVENT from 'shared_data/queries/event/read.graphql';
import LIST_EVENT_RSVP from 'shared_data/queries/event/listRsvps.graphql';

import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';

import { raScreenMd } from '@riseart/antd-provider/dist/website/variables.less';
import {
  eventDetailsPageCls,
  eventDetailsCenteredCls,
  eventDetailsBannerContentCls,
  eventDetailsRsvpContainerCls,
  eventDetailsHighlightCls,
  eventDetailsHighlightRowCls,
  eventDetailsDateContainerCls,
  eventDetailsActionCls,
  eventDetailsAttendeesCls,
  eventDetailsCardExtraCls,
  eventDetailsCardAdditionalCls,
  eventDetailsCardShareCls,
  eventDetailsShareCenteredCls,
} from 'shared_components/pages/event/Details.less';

type Props = {
  staticContext: ?Object,
};

/**
 * getEventFullAddress
 *
 * @param {Object} eventData
 * @param {Function} formatMessage
 * @returns {string}
 */
const getEventFullAddress = (
  { venuePostCode, venueAddress, venueCity, venueCountryCode }: Object,
  formatMessage: Function,
): string => {
  const countryCode = venueCountryCode && venueCountryCode.toUpperCase();

  return [
    venueAddress,
    venueCity,
    venuePostCode,
    countryCode ? formatMessage({ id: `countries.${countryCode}`, default: countryCode }) : null,
  ]
    .filter((data) => !!data)
    .join(', ');
};

/**
 * EventDetailsPage
 *
 * @param {Prop} prop
 */
export default function EventDetailsPage({ staticContext }: Props): Node {
  const history = useHistory();
  const { id: eventId } = useParams();
  const { translatedLocation: location } = useUrlParams();
  const { formatMessage, formatTime } = useIntl();

  return (
    <LayoutDefault scrollTopOnPathChange className={eventDetailsPageCls}>
      <Query
        query={READ_EVENT}
        variables={{ eventId }}
        context={{
          customOptions: {
            errorSuppressFromResponse: true,
            errorHandler: gql404RedirectHandler(
              UrlAssembler.eventList({ search: { message: HTTP_STATUS.NOT_FOUND } }),
              history,
              staticContext,
            ),
          },
        }}
      >
        {({ loading, data, error }) => {
          if (error) {
            return null;
          }

          const { readEvent: eventData } = data || {};
          const image =
            eventData &&
            eventData.images &&
            extractImageFileFromData.eventImage(
              eventData.images,
              ENUM_EVENT.image.type.TYPE_MASTER,
            );

          const canonicalURL = eventData
            ? UrlAssembler.eventDetail(eventData.id, eventData.slug, {
                origin: LocationManager.get('origin'),
              })
            : LocationManager.get('originWithPath');
          const hrefLangParams = eventData
            ? generateHrefLangParams(
                APP_CONFIG.i18n.locales.map(({ name }) => ({
                  localeCode: name,
                  id: eventData.id,
                  slug: eventData.slug,
                })),
              )
            : null;
          const metaData = eventData
            ? {
                title:
                  eventData.metaTitle ||
                  formatMessage({ id: `meta.event.dynamicMetaTitle` }, { name: eventData.name }),
                description:
                  eventData.metaDescription ||
                  formatMessage(
                    { id: `meta.event.dynamicMetaDescription` },
                    { name: eventData.name, address: eventData.address, date: eventData.date },
                  ),
                image: image
                  ? generateSrcForCDN({
                      ...image,
                      width: 1200,
                    })
                  : null,
              }
            : {};
          const eventAddress = eventData ? getEventFullAddress(eventData, formatMessage) : null;
          const { type, ...restEventCardData } = eventData || {};
          const EventCardComponent = eventData ? (
            <EventCard
              showOverlayContent={false}
              event={restEventCardData}
              layout="horizontal"
              size="large"
              clickable={false}
              lazyloadConfig={{
                once: true,
                height: 260,
                offset: 200,
                placeholder: <LazyloadPlaceholder height={260} />,
              }}
              artDirectionSizeKey="artist.eventCol1"
              additionalInfo={
                <Fragment>
                  {type ? (
                    <div className={eventDetailsCardExtraCls}>
                      <EventType id={type} />
                    </div>
                  ) : null}
                  {eventData.creatorName ? (
                    <div className={eventDetailsCardAdditionalCls}>
                      {eventData.artistId
                        ? formatMessage(
                            { id: 'common.byNameLowercaseLink' },
                            {
                              name: eventData.creatorName,
                              link: (chunk) => (
                                <Link
                                  type="light"
                                  to={UrlAssembler.artistProfile(
                                    eventData.artistId,
                                    eventData.artistAlias,
                                  )}
                                  title={eventData.creatorName}
                                >
                                  {chunk}
                                </Link>
                              ),
                            },
                          )
                        : formatMessage(
                            { id: 'common.byNameLowercase' },
                            { name: eventData.creatorName },
                          )}
                    </div>
                  ) : null}
                  <div className={eventDetailsCardShareCls}>
                    <Share
                      theme="dark"
                      contentKey="event"
                      contentValues={{ name: eventData.name }}
                      image={metaData.image}
                      url={canonicalURL}
                      buttonText={formatMessage({ id: 'common.share' }).toUpperCase()}
                    />
                  </div>
                </Fragment>
              }
            />
          ) : null;

          return (
            <IsomorphicRipple isActive={loading}>
              {!loading ? (
                <MetaProvider
                  metaSubscriber={META_ENUM.SUBSCRIBER_NAME.EVENT_META}
                  meta={
                    eventData
                      ? {
                          [META_ENUM.METATYPE.LINK_CANONICAL]: canonicalURL,
                          [META_ENUM.METATYPE.LINK_CANONICAL_REDIRECT]: UrlAssembler.eventDetail(
                            eventData.id,
                            eventData.slug,
                            {
                              ...(location.search ? { search: location.search } : null),
                              ...(location.hash ? { hash: location.hash } : null),
                            },
                          ),
                          [META_ENUM.METATYPE.LINK_HREFLANG]: hrefLangParams,
                          [META_ENUM.METATYPE.LINK_HREFLANG_REDIRECT]: hrefLangParams,
                          [META_ENUM.METATYPE.META_TITLE]: metaData.title,
                          [META_ENUM.METATYPE.META_DESCRIPTION]: metaData.description,
                          [META_ENUM.METATYPE.META_OG_TITLE]: metaData.title,
                          [META_ENUM.METATYPE.META_OG_DESCRIPTION]: metaData.description,
                          [META_ENUM.METATYPE.META_OG_URL]: canonicalURL,
                          [META_ENUM.METATYPE.META_OG_IMAGE]: metaData.image,
                          [META_ENUM.METATYPE.SCRIPT_LD_JSON_EVENT]: generateLDJSON(LDTYPES.EVENT, {
                            name: eventData.name,
                            description: eventData.description,
                            startDate: eventData.startDate,
                            endDate: eventData.endDate,
                            image: metaData.image,
                            url: canonicalURL,
                            venueAddress: eventData.venueAddress,
                            fullAddress: eventAddress,
                            ticketAmount: eventData.ticketAmount,
                            ticketCurrency: eventData.ticketCurrency,
                            externalUrl: eventData.externalUrl,
                            creatorName: eventData.creatorName,
                            creatorUrl: eventData.artistId
                              ? UrlAssembler.artistProfile(
                                  eventData.artistId,
                                  eventData.artistAlias,
                                )
                              : null,
                            mainEntityOfPage: {
                              type: 'WebPage',
                              url: canonicalURL,
                            },
                          }),
                        }
                      : null
                  }
                  hasError={!!error || !data}
                />
              ) : null}
              {eventData ? (
                <Fragment>
                  <MediaQuery minWidth={raScreenMd}>
                    {(isTablet) =>
                      isTablet ? (
                        <BlurredBanner
                          showForTablet
                          content={
                            <Wrapper className={eventDetailsBannerContentCls}>
                              {EventCardComponent}
                            </Wrapper>
                          }
                        >
                          <Picture
                            type="banner.fullWidth"
                            artDirectionKey="common.banner"
                            image={image}
                            alt={eventData.name}
                            lazyload={{
                              once: true,
                              height: 600,
                              offset: 200,
                              placeholder: <LazyloadPlaceholder height={600} />,
                            }}
                          />
                        </BlurredBanner>
                      ) : (
                        EventCardComponent
                      )
                    }
                  </MediaQuery>
                  {eventData.canRsvp ? (
                    <div className={eventDetailsRsvpContainerCls}>
                      <CreateRsvpProvider eventsList={eventData} />
                      <EventsRsvpProvider>
                        <EventRsvpButton eventData={eventData} />
                      </EventsRsvpProvider>
                    </div>
                  ) : null}
                  <Section padding="none">
                    <Wrapper
                      sizes={{
                        xs: 'wide',
                        sm: 'wide',
                        md: 'wide',
                        lg: 'narrow',
                        xl: 'narrow',
                      }}
                    >
                      <Breadcrumbs
                        generateLDJson={false}
                        links={[
                          {
                            name: formatMessage({ id: 'components.breadcrumbs.eventList' }),
                            url: UrlAssembler.eventList(),
                          },
                          ...(eventData
                            ? [
                                {
                                  isLink: false,
                                  name: eventData.name,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </Wrapper>
                  </Section>
                  <Section>
                    <Wrapper
                      sizes={{
                        xs: 'wide',
                        sm: 'wide',
                        md: 'wide',
                        lg: 'narrow',
                        xl: 'narrow',
                      }}
                    >
                      {eventData.description ? (
                        <Row gutter={30}>
                          <Col xs={24} lg={6}>
                            <Heading>
                              {formatMessage({ id: `components.event.headings.description` })}
                            </Heading>
                          </Col>
                          <Col xs={24} lg={18}>
                            <Paragraph>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: nl2br(eventData.description),
                                }}
                              />
                            </Paragraph>
                          </Col>
                        </Row>
                      ) : null}
                    </Wrapper>
                  </Section>
                  <Section background="gray">
                    <Wrapper
                      sizes={{
                        xs: 'wide',
                        sm: 'wide',
                        md: 'wide',
                        lg: 'narrow',
                        xl: 'narrow',
                      }}
                      className={eventDetailsCenteredCls}
                    >
                      <Row gutter={[100, { xs: 20, sm: 20, md: 20, lg: 20, xl: 0 }]}>
                        <Col xs={24} lg={13}>
                          <Row gutter={50}>
                            <Col xs={24} lg={12}>
                              <Heading>
                                {formatMessage({ id: `components.event.headings.dateAndTime` })}
                              </Heading>
                            </Col>
                            <Col xs={24} lg={12}>
                              <div className={eventDetailsDateContainerCls}>
                                <div className={eventDetailsHighlightCls}>
                                  {eventData.startDate ? (
                                    <FormatDate
                                      value={eventData.startDate}
                                      year="numeric"
                                      month="long"
                                      day="numeric"
                                      weekday="short"
                                    />
                                  ) : null}
                                  {eventData.endDate ? (
                                    <Fragment>
                                      {eventData.startDate ? ' - ' : ''}
                                      <FormatDate
                                        value={eventData.endDate}
                                        year="numeric"
                                        month="long"
                                        day="numeric"
                                        weekday="short"
                                      />
                                    </Fragment>
                                  ) : null}
                                </div>
                                <div
                                  className={classNames(
                                    eventDetailsHighlightCls,
                                    eventDetailsHighlightRowCls,
                                  )}
                                >
                                  {eventData.startTime
                                    ? formatTime(setTimeToDate(new Date(), eventData.startTime), {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : null}
                                  {eventData.endTime
                                    ? `${eventData.startTime ? ' - ' : ''}${formatTime(
                                        setTimeToDate(new Date(), eventData.endTime),
                                        {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        },
                                      )}`
                                    : null}
                                </div>
                                <div className={eventDetailsActionCls}>
                                  <EmbeddedGoogleCalendarLink
                                    name={eventData.name}
                                    details={eventData.description}
                                    startDate={eventData.startDate}
                                    startTime={eventData.startTime}
                                    endDate={eventData.endDate}
                                    endTime={eventData.endTime}
                                    location={eventAddress}
                                    linkProps={{
                                      type: 'light',
                                      title: formatMessage({ id: `components.event.addCalendar` }),
                                    }}
                                  >
                                    {formatMessage({ id: `components.event.addCalendar` })}
                                  </EmbeddedGoogleCalendarLink>
                                </div>
                              </div>
                            </Col>
                            {eventData.venueAddress ||
                            eventData.venuePostCode ||
                            eventData.venueCity ||
                            eventData.venuePhone ||
                            eventData.externalUrl ? (
                              <Fragment>
                                <Col xs={24} lg={12}>
                                  <Heading>
                                    {formatMessage({ id: `components.event.headings.address` })}
                                  </Heading>
                                </Col>
                                <Col xs={24} lg={12}>
                                  <div>
                                    {eventData.venueAddress ? (
                                      <div className={eventDetailsHighlightCls}>
                                        {eventData.venueAddress}
                                      </div>
                                    ) : null}
                                    {eventData.venuePostCode || eventData.venueCity
                                      ? [eventData.venuePostCode, eventData.venueCity]
                                          .filter((data) => !!data)
                                          .join(' ')
                                      : null}
                                    {eventData.venuePhone ? (
                                      <Fragment>
                                        <br />
                                        {eventData.venuePhone}
                                      </Fragment>
                                    ) : null}
                                    {eventData.externalUrl ? (
                                      <div className={eventDetailsActionCls}>
                                        <Link
                                          obfuscate
                                          external
                                          href={eventData.externalUrl}
                                          title=""
                                          type="light"
                                          target="_blank"
                                          rel="nofollow"
                                        >
                                          {formatMessage({ id: `components.event.visitSite` })}
                                        </Link>
                                      </div>
                                    ) : null}
                                  </div>
                                </Col>
                              </Fragment>
                            ) : null}
                          </Row>
                        </Col>
                        {eventAddress ? (
                          <Col xs={24} lg={11}>
                            <EmmbeddedGoogleMaps address={eventAddress} height="450px" />
                          </Col>
                        ) : null}
                      </Row>
                    </Wrapper>
                  </Section>
                  <LazyLoad once offset={500}>
                    <Section>
                      <Wrapper
                        sizes={{
                          xs: 'wide',
                          sm: 'wide',
                          md: 'wide',
                          lg: 'narrow',
                          xl: 'narrow',
                        }}
                        className={eventDetailsCenteredCls}
                      >
                        <Query
                          ssr={false}
                          query={LIST_EVENT_RSVP}
                          variables={{ eventId: eventData.id }}
                        >
                          {({ data, loading, error }) => {
                            if (error) {
                              return null;
                            }

                            if (loading) {
                              return <IsomorphicRipple isActive />;
                            }

                            const { listEventRsvps } = data || {};
                            const { items = [] } = listEventRsvps || {};
                            const totalAttendees = (items && items.length) || 0;

                            return (
                              <Row gutter={[50, 50]}>
                                {totalAttendees ? (
                                  <Col xs={24} md={12}>
                                    <Heading>
                                      {formatMessage({
                                        id: `components.event.headings.attendees`,
                                      })}
                                    </Heading>
                                    <div className={eventDetailsAttendeesCls}>
                                      {items.map((item) => (
                                        <Avatar
                                          key={item.id}
                                          images={item.userAvatars}
                                          name={[item.userFirstName, item.userLastName]
                                            .filter((name) => !!name)
                                            .join(' ')}
                                        />
                                      ))}
                                    </div>
                                  </Col>
                                ) : null}
                                <Col
                                  xs={24}
                                  md={totalAttendees ? 12 : 24}
                                  {...(!totalAttendees
                                    ? { className: eventDetailsShareCenteredCls }
                                    : null)}
                                >
                                  <Heading>
                                    {formatMessage({ id: `components.event.headings.share` })}
                                  </Heading>
                                  <div>
                                    <Share
                                      component={ShareList}
                                      hideLabels
                                      theme="dark"
                                      contentKey="event"
                                      contentValues={{ name: eventData.name }}
                                      image={metaData.image}
                                      url={canonicalURL}
                                    />
                                  </div>
                                </Col>
                              </Row>
                            );
                          }}
                        </Query>
                      </Wrapper>
                    </Section>
                  </LazyLoad>
                </Fragment>
              ) : null}
            </IsomorphicRipple>
          );
        }}
      </Query>
    </LayoutDefault>
  );
}
